import React, { useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { topAndTail, formatDateTime } from '../utils/stringUtils';

const TasksPage: React.FC = () => {
  const { state, getCurrentStats } = useApp();
  const navigate = useNavigate();
  const refreshInterval = useRef<NodeJS.Timeout | null>(null);
  
  // Use useMemo to make stats reactive to state changes
  const stats = useMemo(() => getCurrentStats(), [state, getCurrentStats]);
  const tasksData = stats?.tasks;
  const tasks = tasksData?.tasks || [];
  const summary = tasksData?.summary;
  const metrics = tasksData?.metrics || [];

  const connectionPointTasksData = stats?.connection_point_tasks;
  const connectionPointTasks = connectionPointTasksData?.tasks || [];
  const connectionPointSummary = connectionPointTasksData?.summary;

  const mcpProxyTasksData = stats?.mcp_proxy_tasks;
  const mcpProxyTasks = mcpProxyTasksData?.tasks || [];
  const mcpProxySummary = mcpProxyTasksData?.summary;

  const pipeTasksData = stats?.pipe_tasks;
  const pipeTasks = pipeTasksData?.tasks || [];
  const pipeTasksSummary = pipeTasksData?.summary;

  // Pipe metrics from the standard metrics endpoint
  const pipeStats = stats?.metrics?.pipe_stats || [];

  // Auto-refresh every 5 seconds
  useEffect(() => {
    refreshInterval.current = setInterval(() => {
      // The data will update automatically via WebSocket
    }, 5000);

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return bytes.toFixed(0) + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  };

  // Create metrics map for quick lookup
  const metricsMap: Record<string, any> = {};
  metrics.forEach(m => {
    if (m?.task_id) {
      metricsMap[m.task_id] = m;
    }
  });

  // Calculate aggregate throughput
  const aggregateThroughput = metrics.reduce((total, metric) => {
    return total + (metric?.throughput_bytes_per_sec || 0);
  }, 0);

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>

      <div className="card shadow mb-4">
        <div className="card-header py-3">

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-tasks"></i> Channel Tasks
              </h6>
              <small className="text-muted">Activity scope: 60 seconds</small>
            </div>
            <div>
              <button className="btn btn-sm btn-primary mr-2" onClick={() => window.location.reload()}>
                <i className="fas fa-sync-alt"></i> Refresh
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          {/* Task Summary Cards */}
          <div className="row mb-4">
            <div className="col-xl-3 col-md-6 mb-3">
              <div className="card border-left-primary h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                        Total Tasks
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {summary?.total_tasks || tasks.length}
                      </div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-tasks fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 mb-3">
              <div className="card border-left-info h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                        Active Connections
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {summary?.total_active_connections || 0}
                      </div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-network-wired fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 mb-3">
              <div className="card border-left-warning h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                        Total Data Transfer
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {formatBytes(summary?.total_bytes_transferred || 0)}
                      </div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-exchange-alt fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 mb-3">
              <div className="card border-left-success h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                        Aggregate Throughput
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {aggregateThroughput > 0 ? `${formatBytes(aggregateThroughput)}/s` : '0 B/s'}
                      </div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-tachometer-alt fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Table */}
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th>Channel Name</th>
                  <th>Listen Address</th>
                  <th>Target Endpoint</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Uptime</th>
                  <th className="text-center">Connections</th>
                  <th className="text-center">Active</th>
                  <th className="text-center">Total Bytes</th>
                  <th className="text-center">Throughput</th>
                  <th className="text-center">Errors</th>
                  <th className="text-center">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center text-muted">No running tasks</td>
                  </tr>
                ) : (
                  tasks
                    .sort((a, b) => (a.channel_name || '').toLowerCase().localeCompare((b.channel_name || '').toLowerCase()))
                    .map((task: any, index: number) => {
                      const taskMetrics = metricsMap[task.task_id || ''] || {};
                      
                      // Status badge
                      const getStatusBadge = () => {
                        switch (task.status) {
                          case 'running':
                            return <span className="badge badge-success"><i className="fas fa-play-circle"></i> Running</span>;
                          case 'starting':
                            return <span className="badge badge-info"><i className="fas fa-circle-notch fa-spin"></i> Starting</span>;
                          case 'stopping':
                            return <span className="badge badge-warning"><i className="fas fa-stop-circle"></i> Stopping</span>;
                          case 'error':
                            return <span className="badge badge-danger"><i className="fas fa-exclamation-circle"></i> Error</span>;
                          default:
                            return <span className="badge badge-secondary">Unknown</span>;
                        }
                      };
                      
                      // Calculate uptime
                      const getUptimeDisplay = () => {
                        if (!task.started_at) return <span className="text-muted">-</span>;
                        try {
                          const startedAt = new Date(task.started_at);
                          const now = new Date();
                          const uptimeMs = now.getTime() - startedAt.getTime();
                          return formatDuration(uptimeMs);
                        } catch (e) {
                          return <span className="text-muted">-</span>;
                        }
                      };
                      
                      // Format last activity
                      const getLastActivity = () => {
                        if (!task.last_activity) return <span className="text-muted">Never</span>;
                        try {
                          return formatTimeAgo(new Date(task.last_activity));
                        } catch (e) {
                          return <span className="text-muted">Invalid date</span>;
                        }
                      };
                      
                      // Format total bytes transferred
                      const getTotalBytes = () => {
                        const bytesSent = typeof task.bytes_sent === 'number' ? task.bytes_sent : 0;
                        const bytesReceived = typeof task.bytes_received === 'number' ? task.bytes_received : 0;
                        const totalBytes = bytesSent + bytesReceived;
                        
                        return totalBytes > 0 ? formatBytes(totalBytes) : <span className="text-muted">N/A</span>;
                      };
                      
                      // Error count
                      const errorCount = typeof task.error_count === 'number' ? task.error_count : 0;
                      const getErrorBadge = () => {
                        return errorCount > 0 ?
                          <span className="badge badge-danger" title={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}>{errorCount}</span> :
                          <span className="text-muted">0</span>;
                      };
                      
                      return (
                        <tr key={task.task_id || index}>
                          <td><strong>{task.channel_name || 'Unknown'}</strong></td>
                          <td><code className="small">{(task.listen_address && task.listen_address.startsWith('did:')) 
                            ? topAndTail(task.listen_address, 16, 24)
                            : (task.listen_address || '-')}</code></td>
                          <td><code className="small">{(task.target_endpoint && task.target_endpoint.startsWith('did:')) 
                            ? topAndTail(task.target_endpoint, 16, 24)
                            : (task.target_endpoint || '-')}</code></td>
                          <td className="text-center">{getStatusBadge()}</td>
                          <td className="text-center" title={task.started_at ? `Started: ${formatDateTime(task.started_at, true)}` : 'Start time unknown'}>
                            {getUptimeDisplay()}
                          </td>
                          <td className="text-center">
                            {typeof task.total_connections === 'number' ? task.total_connections : <span className="text-muted">-</span>}
                          </td>
                          <td className="text-center">
                            {typeof task.active_connections === 'number' ?
                              (task.active_connections > 0 ?
                                <span className="badge badge-info">{task.active_connections}</span> :
                                <span className="text-muted">0</span>
                              ) :
                              <span className="text-muted">-</span>
                            }
                          </td>
                          <td className="text-center">{getTotalBytes()}</td>
                          <td className="text-center" title={`Current throughput: ${taskMetrics.throughput_bytes_per_sec ? formatBytes(taskMetrics.throughput_bytes_per_sec) + '/sec' : 'No current activity'}`}>
                            {taskMetrics.throughput_bytes_per_sec && taskMetrics.throughput_bytes_per_sec > 0 ?
                              `${formatBytes(taskMetrics.throughput_bytes_per_sec)}/s` :
                              <span className="text-muted">0 B/s</span>
                            }
                          </td>
                          <td className="text-center">{getErrorBadge()}</td>
                          <td className="text-center"><small>{getLastActivity()}</small></td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Connection Point Tasks */}
      {connectionPointTasks.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-plug"></i> Connection Point Tasks
                </h6>
                <small className="text-muted">WebSocket listeners for gateway connection points</small>
              </div>
            </div>
          </div>
          <div className="card-body">
            {/* Connection Point Summary Cards */}
            <div className="row mb-4">
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-primary h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                          Total Listeners
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.total_listeners || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-plug fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-success h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                          Connected
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.connected || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-warning h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                          Reconnecting
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.reconnecting || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-sync-alt fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-danger h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                          Failed
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.failed || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-exclamation-circle fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-info h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                          Messages
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.total_messages || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-envelope fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 mb-3">
                <div className="card border-left-danger h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                          Errors
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {connectionPointSummary?.total_errors || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-exclamation-triangle fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection Point Tasks Table */}
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="text-center">Type</th>
                    <th>Gateway DID</th>
                    <th>Mediator DID</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Uptime</th>
                    <th className="text-center">Messages</th>
                    <th className="text-center">Errors</th>
                    <th className="text-center">Reconnect Attempts</th>
                    <th className="text-center">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {connectionPointTasks
                    .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
                    .map((task: any, index: number) => {
                      // Status badge
                      const getStatusBadge = () => {
                        switch (task.status) {
                          case 'connected':
                            return <span className="badge badge-success"><i className="fas fa-check-circle"></i> Connected</span>;
                          case 'reconnecting':
                            return <span className="badge badge-warning"><i className="fas fa-sync-alt fa-spin"></i> Reconnecting</span>;
                          case 'failed':
                            return <span className="badge badge-danger"><i className="fas fa-exclamation-circle"></i> Failed</span>;
                          default:
                            return <span className="badge badge-secondary">Unknown</span>;
                        }
                      };
                      
                      // Calculate uptime
                      const getUptimeDisplay = () => {
                        if (!task.started_at) return <span className="text-muted">-</span>;
                        try {
                          const startedAt = new Date(task.started_at);
                          const now = new Date();
                          const uptimeMs = now.getTime() - startedAt.getTime();
                          return formatDuration(uptimeMs);
                        } catch (e) {
                          return <span className="text-muted">-</span>;
                        }
                      };
                      
                      // Format last activity
                      const getLastActivity = () => {
                        if (!task.last_activity) return <span className="text-muted">Never</span>;
                        try {
                          return formatTimeAgo(new Date(task.last_activity));
                        } catch (e) {
                          return <span className="text-muted">Invalid date</span>;
                        }
                      };
                      
                      // Get task type badge with appropriate icon and color
                      const getTaskTypeBadge = () => {
                        switch (task.task_type) {
                          case 'User-Created':
                            return <span className="badge badge-success" title="User-created connection point"><i className="fas fa-user"></i> User</span>;
                          case 'OOB Inviter':
                            return <span className="badge badge-info" title="Permanent connection from OOB invitation (inviter side)"><i className="fas fa-paper-plane"></i> OOB Inviter</span>;
                          case 'OOB Responder':
                            return <span className="badge badge-warning" title="Auto-created for receiving from OOB acceptor (inviter side)"><i className="fas fa-reply"></i> OOB Responder</span>;
                          case 'OOB Acceptor':
                            return <span className="badge badge-primary" title="Gateway listener endpoint (acceptor side)"><i className="fas fa-envelope-open"></i> OOB Acceptor</span>;
                          case 'System':
                            return <span className="badge badge-secondary" title="Legacy system-created connection point"><i className="fas fa-cog"></i> System</span>;
                          default:
                            return <span className="badge badge-secondary"><i className="fas fa-headphones"></i> {task.task_type || 'Unknown'}</span>;
                        }
                      };
                      
                      return (
                        <tr key={task.id || index}>
                          <td><strong>{task.name || 'Unknown'}</strong></td>
                          <td className="text-center">
                            {getTaskTypeBadge()}
                          </td>
                          <td><code className="small">{topAndTail(task.gateway_did, 16, 24)}</code></td>
                          <td><code className="small">{topAndTail(task.mediator_did, 16, 24)}</code></td>
                          <td className="text-center">{getStatusBadge()}</td>
                          <td className="text-center" title={task.started_at ? `Started: ${formatDateTime(task.started_at, true)}` : 'Start time unknown'}>
                            {getUptimeDisplay()}
                          </td>
                          <td className="text-center">
                            {typeof task.message_count === 'number' ? 
                              (task.message_count > 0 ?
                                <span className="badge badge-info">{task.message_count}</span> :
                                <span className="text-muted">0</span>
                              ) : <span className="text-muted">-</span>
                            }
                          </td>
                          <td className="text-center">
                            {typeof task.error_count === 'number' && task.error_count > 0 ?
                              <span className="badge badge-danger" title={`${task.error_count} error${task.error_count !== 1 ? 's' : ''}`}>{task.error_count}</span> :
                              <span className="text-muted">0</span>
                            }
                          </td>
                          <td className="text-center">
                            {typeof task.reconnect_attempts === 'number' && task.reconnect_attempts > 0 ?
                              <span className="badge badge-warning">{task.reconnect_attempts}</span> :
                              <span className="text-muted">0</span>
                            }
                          </td>
                          <td className="text-center"><small>{getLastActivity()}</small></td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MCP Proxy Tasks */}
      {mcpProxyTasks.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-plug"></i> MCP Proxy Tasks
                </h6>
                <small className="text-muted">MCP protocol proxies to REST APIs</small>
              </div>
            </div>
          </div>
          <div className="card-body">
            {/* MCP Proxy Summary Cards */}
            <div className="row mb-4">
              <div className="col-xl-4 col-md-6 mb-3">
                <div className="card border-left-primary h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                          Total Proxies
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {mcpProxySummary?.total_proxies || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-plug fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-md-6 mb-3">
                <div className="card border-left-success h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                          Active
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {mcpProxySummary?.active || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-md-6 mb-3">
                <div className="card border-left-secondary h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">
                          Disabled
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {mcpProxySummary?.disabled || 0}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-ban fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* MCP Proxy Tasks Table */}
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Proxy Path</th>
                    <th>Target API</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {mcpProxyTasks.map((task: any, index: number) => {
                    const getStatusBadge = () => {
                      switch (task.status) {
                        case 'active':
                          return <span className="badge badge-success"><i className="fas fa-play-circle"></i> Active</span>;
                        case 'disabled':
                          return <span className="badge badge-secondary"><i className="fas fa-stop-circle"></i> Disabled</span>;
                        default:
                          return <span className="badge badge-secondary">Unknown</span>;
                      }
                    };

                    const getCreatedDate = () => {
                      if (!task.created_at) return <span className="text-muted">-</span>;
                      try {
                        return formatTimeAgo(new Date(task.created_at));
                      } catch (e) {
                        return <span className="text-muted">Invalid date</span>;
                      }
                    };

                    return (
                      <tr key={task.id || index}>
                        <td><strong>{task.name || 'Unknown'}</strong></td>
                        <td><small>{task.description || '-'}</small></td>
                        <td><code className="small">{task.proxy_path || '-'}</code></td>
                        <td><code className="small">{task.base_url || '-'}</code></td>
                        <td className="text-center">{getStatusBadge()}</td>
                        <td className="text-center" title={task.created_at ? `Created: ${formatDateTime(task.created_at, true)}` : 'Unknown'}>
                          <small>{getCreatedDate()}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* LLM Pipe Metrics Section */}
      {pipeStats && pipeStats.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="m-0 font-weight-bold text-success">
                  <i className="fas fa-brain"></i> LLM Pipe Metrics
                </h6>
                <small className="text-muted">Agent-to-external-service LLM calls</small>
              </div>
            </div>
          </div>
          <div className="card-body">
            {/* Pipe Summary Cards */}
            <div className="row mb-4">
              <div className="col-xl-3 col-md-6 mb-3">
                <div className="card border-left-success h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                          Active Pipes
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {pipeStats.length}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-brain fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-3">
                <div className="card border-left-warning h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                          Total Requests
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {pipeStats.reduce((sum, p) => sum + p.total_requests, 0).toLocaleString()}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-arrow-right fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-3">
                <div className="card border-left-info h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                          Total Tokens
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {(() => {
                            const total = pipeStats.reduce((sum, p) => sum + p.total_input_tokens + p.total_output_tokens, 0);
                            if (total >= 1000000) return `${(total / 1000000).toFixed(2)}M`;
                            if (total >= 1000) return `${(total / 1000).toFixed(2)}K`;
                            return total.toLocaleString();
                          })()}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-coins fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-3">
                <div className="card border-left-danger h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                          Total Cost
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          ${pipeStats.reduce((sum, p) => sum + p.total_cost, 0).toFixed(4)}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pipe Metrics Table */}
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Pipe Name</th>
                    <th>Provider</th>
                    <th>Model</th>
                    <th className="text-center">Requests</th>
                    <th className="text-center">Success Rate</th>
                    <th className="text-center">Tokens (In/Out)</th>
                    <th className="text-center">Cost</th>
                    <th className="text-center">Avg Latency</th>
                    <th className="text-center">Request Latency</th>
                    <th className="text-center">Response Latency</th>
                    <th className="text-center">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {pipeStats.map((pipe: any, index: number) => {
                    const successRate = pipe.total_requests > 0 
                      ? ((pipe.successful_requests / pipe.total_requests) * 100).toFixed(1)
                      : '0.0';
                    
                    const formatTokens = (tokens: number): string => {
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(2)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toLocaleString();
                    };

                    const getLastActivity = () => {
                      if (!pipe.last_activity) return <span className="text-muted">Never</span>;
                      try {
                        return formatTimeAgo(new Date(pipe.last_activity));
                      } catch (e) {
                        return <span className="text-muted">Invalid</span>;
                      }
                    };

                    const getSuccessRateBadge = () => {
                      const rate = parseFloat(successRate);
                      if (rate >= 95) {
                        return <span className="badge badge-success">{successRate}%</span>;
                      } else if (rate >= 80) {
                        return <span className="badge badge-warning">{successRate}%</span>;
                      } else {
                        return <span className="badge badge-danger">{successRate}%</span>;
                      }
                    };

                    return (
                      <tr key={pipe.pipe_id || index}>
                        <td>
                          <strong>{pipe.pipe_name || 'Unnamed'}</strong>
                          <br/><small className="text-muted">{topAndTail(pipe.pipe_id, 8, 8)}</small>
                        </td>
                        <td>
                          <span className="badge badge-info">{pipe.provider}</span>
                        </td>
                        <td><code className="small">{pipe.model}</code></td>
                        <td className="text-center">
                          <span className="badge badge-primary">{pipe.total_requests.toLocaleString()}</span>
                          {pipe.failed_requests > 0 && (
                            <><br/><span className="badge badge-danger" title="Failed requests">{pipe.failed_requests}</span></>
                          )}
                        </td>
                        <td className="text-center">{getSuccessRateBadge()}</td>
                        <td className="text-center">
                          <span className="text-success" title="Input tokens">{formatTokens(pipe.total_input_tokens)}</span>
                          {' / '}
                          <span className="text-info" title="Output tokens">{formatTokens(pipe.total_output_tokens)}</span>
                        </td>
                        <td className="text-center">
                          <strong>${pipe.total_cost.toFixed(4)}</strong>
                        </td>
                        <td className="text-center">
                          <span className={pipe.avg_latency_ms > 1000 ? 'text-warning' : 'text-success'}>
                            {pipe.avg_latency_ms.toFixed(0)}ms
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="text-info" title="Gateway processing time">
                            {pipe.avg_gateway_processing_ms ? `${pipe.avg_gateway_processing_ms.toFixed(0)}ms` : '-'}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="text-primary" title="Target (LLM) processing time">
                            {pipe.avg_target_processing_ms ? `${pipe.avg_target_processing_ms.toFixed(0)}ms` : '-'}
                          </span>
                        </td>
                        <td className="text-center">
                          <small>{getLastActivity()}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;